import {
  DataBlockVariant,
  DoctorType,
  MergePatientsDto,
  NumberSource,
  PatchPatientDto,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';

import {
  CustomDictionaryName,
  useDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';

import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import globalStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { getFormattedDate } from '@lmt/unicorn-shared/app/components/patients/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { localizeSexType } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import clsx from 'clsx';
import i18next from 'i18next';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  CustomFieldProps,
  FieldStatus,
  getNumberPostfix,
  getPatientCustomFields,
  getPatientDict,
  getPatientFields,
  getUpdatedPatient,
  isEmpty,
} from './components/common';

import styles from './components/MergePair.module.scss';
import { MergePatientsFieldsTable } from './components/MergePatientsFieldsTable';
import { MergePatientsResult } from './components/MergePatientsResult';
import { MergePatientsSearch } from './components/MergePatientsSearch';
import { SearchPatientsFieldsTable } from './components/SearchPatientsFieldsTable';

export interface MergePairProps {
  firstPatientId?: number;
  secondPatientId?: number;
  patientDuplicatePairId?: number;
}

export const MergePair: FC<MergePairProps & FormComponentProps> = (props) => {
  const { formType, formId, tryCompleteForm } = props;
  const queryClient = useQueryClient();
  const i18nMerge = useScopedTranslation('merge_assistant.merge_pair');
  const tabs = useTabs();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const forms = useGlobalForms();

  const [patientFields, setPatientFields] = useState<{
    [key: string]: FieldStatus;
  }>({});
  const [patientCustomFields, setPatientCustomFields] = useState<{
    [key: string]: FieldStatus;
  }>({});

  const [firstPatientId, setFirstPatientId] = useState<number | undefined>(
    props.firstPatientId,
  );
  const [secondPatientId, setSecondPatientId] = useState<number | undefined>(
    props.secondPatientId,
  );
  const [isOpenPatient, setOpenPatient] = useState<boolean>(true);

  const dataBlockTypes = useEnabledDataBlockTypes();
  const patientDataBlockType = dataBlockTypes.find(
    (x) => x.variant === DataBlockVariant.SharedFields && x.value === 'Patient',
  );
  if (!patientDataBlockType) {
    throw new Error("DataBlockType for Patient wasn't found");
  }

  const patientSharedFieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId: patientDataBlockType.id },
    {
      enabled: !!patientDataBlockType,
    },
  );

  const patient1Query = QueryFactory.PatientQuery.useGetPatientQuery(
    { id: firstPatientId! },
    {
      enabled: !!firstPatientId,
    },
  );

  const patient2Query = QueryFactory.PatientQuery.useGetPatientQuery(
    secondPatientId!,
    {
      enabled: !!secondPatientId,
    },
  );

  const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery({
    all: true,
    doctorType: DoctorType.External,
  });
  const insuranceCompaniesQuery = useDictionaryValuesByTitle(
    CustomDictionaryName.InsuranceCompanies,
  );
  const titleDictQuery = useDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
  );

  const patient1Dict = useMemo(() => {
    return getPatientDict(patient1Query?.data);
  }, [patient1Query?.data]);

  const patient2Dict = useMemo(() => {
    return getPatientDict(patient2Query?.data);
  }, [patient2Query?.data]);

  useEffect(() => {
    if (
      !patient1Query?.data ||
      !patient2Query?.data ||
      !patientSharedFieldsQuery.data
    ) {
      return;
    }

    const patient1Dict = getPatientDict(patient1Query?.data);
    const patient2Dict = getPatientDict(patient2Query?.data);
    setPatientFields(getPatientFields(patient1Dict, patient2Dict));
    setPatientCustomFields(
      getPatientCustomFields(
        patient1Query.data?.values ?? {},
        patient2Query.data?.values ?? {},
        patientSharedFieldsQuery.data ?? [],
      ),
    );
  }, [
    patient1Query?.data?.id,
    patient2Query?.data?.id,
    !!patientSharedFieldsQuery.data,
  ]);

  const changeFieldStatus = useCallback((key: string, value: FieldStatus) => {
    setPatientFields((prevValues) => {
      return {
        ...prevValues,
        [key]: value,
      };
    });
  }, []);

  const changeCustomFieldStatus = useCallback(
    (key: string, value: FieldStatus) => {
      setPatientCustomFields((prevValues) => {
        return {
          ...prevValues,
          [key]: value,
        };
      });
    },
    [],
  );

  const selectAll = useCallback(
    (status: FieldStatus) => {
      for (const field in patientFields) {
        if (
          patientFields[field] !== FieldStatus.const &&
          ((!isEmpty(patient1Dict[field]) && !isEmpty(patient2Dict[field])) ||
            (status === FieldStatus.firstPatient &&
              !isEmpty(patient1Dict[field])) ||
            (status === FieldStatus.secondPatient &&
              !isEmpty(patient2Dict[field])))
        ) {
          changeFieldStatus(field, status);
        }
      }
      for (const field in patientCustomFields) {
        if (
          patientCustomFields[field] !== FieldStatus.const &&
          ((!isEmpty(patient1Dict['customFields'][field]) &&
            !isEmpty(patient2Dict['customFields'][field])) ||
            (status === FieldStatus.firstPatient &&
              !isEmpty(patient1Dict['customFields'][field])) ||
            (status === FieldStatus.secondPatient &&
              !isEmpty(patient2Dict['customFields'][field])))
        ) {
          changeCustomFieldStatus(field, status);
        }
      }
    },
    [patientFields, patient1Dict, patient2Dict],
  );

  const onErrorHandle = useCallback(
    async (err: any) => {
      if (err.status === 404) {
        await modals.showError({ text: i18nMerge.t('error_patients_merged') });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey(),
        });
        await tryCompleteForm(formType, formId, async () => undefined);
      } else {
        await modals.showError({ text: parseError(err) });
      }
    },
    [formType, formId, tryCompleteForm, dispatch],
  );

  const mergePatients = useCallback(async () => {
    const isConfirmed = await modals.showConfirm({
      title: i18nMerge.t('merge_confirmation_title'),
      text: i18nMerge.t('merge_confirmation_text'),
      okButtonText: i18nMerge.t('merge_confirmation_ok_button'),
    });
    if (isConfirmed) {
      try {
        await tryCompleteForm(
          formType,
          formId,
          async () => {
            if (firstPatientId && secondPatientId) {
              const updatedPatientId =
                patientFields['number'] === FieldStatus.firstPatient
                  ? firstPatientId
                  : secondPatientId;
              const deletedPatientId =
                updatedPatientId === firstPatientId
                  ? secondPatientId
                  : firstPatientId;
              const updatedPatient: PatchPatientDto = getUpdatedPatient({
                fields: patientFields,
                customFields: patientCustomFields,
                patient1Dict: patient1Dict,
                patient2Dict: patient2Dict,
                patient1CustomFields: patient1Query.data?.values ?? {},
                patient2CustomFields: patient2Query.data?.values ?? {},
              });
              const dto: MergePatientsDto = {
                updatedPatientId: updatedPatientId,
                removedPatientId: deletedPatientId,
                patchPatient: updatedPatient,
              };
              await QueryFactory.PatientClient.mergePatients(dto);
              dispatch(NavigationActions.patientRemoved(deletedPatientId));
              dispatch(NavigationActions.patientRemoved(updatedPatientId));
              queryClient.setQueryData(
                QueryFactory.PatientQuery.getPatientQueryKey(updatedPatientId),
                updatedPatient,
              );
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey(),
              });
              if (isOpenPatient) {
                tabs.openTab(
                  pages.patientProfile.link({
                    patientId: updatedPatientId,
                  }),
                );
                forms.openPatientEditing(updatedPatientId);
              }
              toast(i18nMerge.t('merge_successful_toast'), {
                type: 'success',
              });
            }
          },
          i18nMerge.t('saving'),
        );
      } catch (err) {
        await onErrorHandle(err);
      }
    }
  }, [
    formType,
    formId,
    tryCompleteForm,
    patientFields,
    patientCustomFields,
    patient1Dict,
    patient2Dict,
    firstPatientId,
    secondPatientId,
    patient1Query.data,
    patient2Query.data,
    dispatch,
    isOpenPatient,
  ]);

  const onDecline = useCallback(async () => {
    try {
      if (
        (await modals.showConfirm({
          title: i18nMerge.t('decline_confirmation_title'),
          text: i18nMerge.t('decline_confirmation_text'),
        })) !== true
      )
        return;
      await tryCompleteForm(
        formType,
        formId,
        async () => {
          if (props.patientDuplicatePairId) {
            await QueryFactory.PatientDuplicateClient.remove(
              props.patientDuplicatePairId,
            );
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey(),
            });
          }
        },
        i18nMerge.t('saving'),
      );
    } catch (err) {
      await onErrorHandle(err);
    }
  }, [
    formType,
    formId,
    tryCompleteForm,
    props.patientDuplicatePairId,
    dispatch,
  ]);

  function getValue(fieldName: string, patientDict: { [key: string]: any }) {
    const value = patientDict[fieldName];
    if (isEmpty(value)) {
      return undefined;
    } else if (fieldName === 'doctorId') {
      const doctor = (doctorsQuery.data?.data ?? []).find(
        (doctor) => doctor.id === value,
      );
      if (doctor) {
        return doctor.name;
      } else {
        return undefined;
      }
    } else if (fieldName === 'healthInsuranceCompanyId') {
      const company = (insuranceCompaniesQuery.data?.data ?? []).find(
        (company) => company.id === value,
      );
      if (company) {
        return company.value;
      } else {
        return undefined;
      }
    } else if (fieldName === 'number') {
      const postfix = getNumberPostfix(
        patientDict['numberSource'] as NumberSource,
        i18nMerge.t('external_patient_number'),
      );
      return value + postfix;
    } else if (fieldName === 'dateOfBirth' || fieldName === 'dateOfDeath') {
      return getFormattedDate(value);
    } else if (fieldName === 'sex') {
      return localizeSexType(value);
    }
    return value.toString();
  }

  function getCustomValue(
    fieldName: string,
    customFields: { [key: string]: any } | undefined,
  ): CustomFieldProps {
    if (!customFields) {
      return { value: null };
    }
    let value = customFields[fieldName];
    if (fieldName === 'Title') {
      const title = (titleDictQuery.data?.data ?? []).find(
        (title) => title.id === value,
      );
      if (title) {
        value = i18next.t(title.value as any, { ns: 'data-block-fields' });
      }
    }
    const field = (patientSharedFieldsQuery.data || []).find(
      (field) => fieldName === field.name,
    );
    if (!field || isEmpty(value)) {
      return { value: null };
    }
    return {
      value: value,
      dataType: field.dataType,
      dictionaryId: field.dictionaryId ?? undefined,
      columns: field.tableColumns ?? undefined,
    };
  }

  return (
    <div
      className={clsx(
        globalStyles.autoSizedContainer,
        globalStyles.autoSizedContainerWithScroll,
      )}
      data-test-id="mergePairPopup"
    >
      <div className={styles.dataContainer}>
        <div className={styles.rootGrid}>
          <MergePatientsSearch
            defaultFirstPatientId={props.firstPatientId}
            defaultSecondPatientId={props.secondPatientId}
            setFirstPatientId={setFirstPatientId}
            setSecondPatientId={setSecondPatientId}
          />
          {!patient2Query.data || !patient1Query.data ? (
            <SearchPatientsFieldsTable
              firstPatient={patient1Query.data}
              secondPatient={patient2Query.data}
            />
          ) : (
            <MergePatientsFieldsTable
              fields={patientFields}
              customFields={patientCustomFields}
              changeField={changeFieldStatus}
              changeCustomField={changeCustomFieldStatus}
              getFirstPatientValue={(fieldName) =>
                getValue(fieldName, patient1Dict)
              }
              getSecondPatientValue={(fieldName) =>
                getValue(fieldName, patient2Dict)
              }
              getFirstPatientCustomValue={(fieldName) =>
                getCustomValue(fieldName, patient1Query.data?.values)
              }
              getSecondPatientCustomValue={(fieldName) =>
                getCustomValue(fieldName, patient2Query.data?.values)
              }
              selectAll={selectAll}
            />
          )}
        </div>
        <MergePatientsResult
          fields={patientFields}
          customFields={patientCustomFields}
          getFirstPatientValue={(fieldName) =>
            getValue(fieldName, patient1Dict)
          }
          getSecondPatientValue={(fieldName) =>
            getValue(fieldName, patient2Dict)
          }
          getFirstPatientCustomValue={(fieldName) =>
            getCustomValue(fieldName, patient1Query.data?.values)
          }
          getSecondPatientCustomValue={(fieldName) =>
            getCustomValue(fieldName, patient2Query.data?.values)
          }
        />
      </div>
      <div
        className={clsx(globalStyles.submitContainer, styles.controlsContainer)}
      >
        <CheckBox
          name={'isOpenPatient'}
          title={i18nMerge.t('open_patient')}
          checked={isOpenPatient}
          onChange={() => setOpenPatient(!isOpenPatient)}
          className={styles.checkbox}
        />
        <div className={styles.controls}>
          {props.patientDuplicatePairId && (
            <Button
              className={globalStyles.submit}
              title={i18nMerge.t('decline_button')}
              onClick={onDecline}
              color={ButtonColor.Primary}
            />
          )}
          <Button
            disabled={
              Object.values(patientFields).includes(FieldStatus.notSelected) ||
              Object.values(patientCustomFields).includes(
                FieldStatus.notSelected,
              ) ||
              !firstPatientId ||
              !secondPatientId
            }
            className={globalStyles.submit}
            title={i18nMerge.t('confirm_button')}
            onClick={mergePatients}
          />
        </div>
      </div>
    </div>
  );
};
